import type { LinkProps } from "@tanstack/react-router"
import type { LucideIcon } from "lucide-react"

import {
  Cake,
  CalendarClock,
  CalendarDays,
  CalendarOff,
  CalendarRange,
  ClipboardList,
  Crosshair,
  Megaphone,
  Radar,
  Swords,
  TrendingUp,
  Users,
} from "lucide-react"

export type AdminNavEntry = {
  kind: "link"
  key: string
  to: LinkProps["to"]
  label: string
  description: string
  icon: LucideIcon
  featured?: boolean
}

export type AdminNavSubmenu = {
  kind: "submenu"
  key: string
  label: string
  icon: LucideIcon
  items: AdminNavEntry[]
}

export type AdminNavItem = AdminNavEntry | AdminNavSubmenu

export type AdminNavSection = {
  key: string
  label: string
  items: AdminNavItem[]
}

export const adminNavSections: AdminNavSection[] = [
  {
    key: "guild-operations",
    label: "Guild Operations",
    items: [
      {
        kind: "link",
        key: "members",
        to: "/members",
        label: "Members",
        description: "Manage member profiles, titles, and monthly points",
        icon: Users,
      },
      {
        kind: "link",
        key: "absence-review",
        to: "/absence-review",
        label: "Absence Review",
        description:
          "Track active absences and clear pending submissions before roster planning",
        icon: CalendarOff,
      },
      {
        kind: "link",
        key: "guild-events",
        to: "/guild-events",
        label: "Guild Events",
        description:
          "Create and operate guild event announcements, signups, and lifecycle transitions",
        icon: CalendarDays,
        featured: true,
      },
      {
        kind: "link",
        key: "daily-content",
        to: "/daily-content",
        label: "Daily Content",
        description:
          "Configure recurring QOTD and FOTD profiles with scheduler-backed publish controls",
        icon: CalendarClock,
        featured: true,
      },
      {
        kind: "link",
        key: "activity-levels",
        to: "/activity-levels",
        label: "Activity Levels",
        description:
          "Configure XP tracks, level rewards, and member progression",
        icon: TrendingUp,
      },
      {
        kind: "link",
        key: "birthdays",
        to: "/birthdays",
        label: "Birthdays",
        description: "Member birthday announcements & role",
        icon: Cake,
      },
      {
        kind: "submenu",
        key: "boss-talents",
        label: "Boss Talents",
        icon: Radar,
        items: [
          {
            kind: "link",
            key: "talent-config",
            to: "/talent-config",
            label: "Talent Config",
            description:
              "Tune talent data, boss cards, and progression guidance shown to players",
            icon: Crosshair,
          },
          {
            kind: "link",
            key: "talent-overview",
            to: "/talent-overview",
            label: "Talent Overview",
            description:
              "Review who still needs boss clears and key martial arts unlocks",
            icon: Radar,
          },
        ],
      },
      {
        kind: "submenu",
        key: "guild-war-admin",
        label: "Guild War Admin",
        icon: Swords,
        items: [
          {
            kind: "link",
            key: "candidate-pool",
            to: "/guild-war/candidate-pool",
            label: "Candidate Pool",
            description:
              "Sort applicants and active members by readiness, path, and availability",
            icon: Users,
          },
          {
            kind: "link",
            key: "weeks",
            to: "/guild-war/weeks",
            label: "Week Management",
            description:
              "Schedule the guild's weekly event rhythm and keep each cycle on track",
            icon: CalendarRange,
          },
          {
            kind: "link",
            key: "permanent-membership",
            to: "/guild-war/permanent-membership",
            label: "Permanent Membership",
            description:
              "Review applications and confirm which members currently hold permanent guild war slots",
            icon: ClipboardList,
          },
          {
            kind: "link",
            key: "team-board",
            to: "/guild-war/team-board",
            label: "GW Roster",
            description:
              "Assemble lineups and role coverage for coordinated group content",
            icon: Swords,
            featured: true,
          },
          {
            kind: "link",
            key: "command-center",
            to: "/guild-war/actions",
            label: "Command Center",
            description:
              "Send DM reminders to unsigned members and trigger one-off guild war actions",
            icon: Megaphone,
          },
        ],
      },
    ],
  },
]

const flattenEntries = (items: AdminNavItem[]): AdminNavEntry[] =>
  items.flatMap((item) => (item.kind === "submenu" ? item.items : [item]))

export const allAdminNavEntries = adminNavSections.flatMap((section) =>
  flattenEntries(section.items),
)
